import { useState } from 'react';
import { Share2, Check, Copy, Link as LinkIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

interface ShareCalculationButtonProps {
  title: string;
  params: Record<string, string | number | boolean | undefined>;
  className?: string;
  variant?: 'outline' | 'ghost' | 'default';
  size?: 'default' | 'sm' | 'lg' | 'icon';
}

export function ShareCalculationButton({
  title,
  params,
  className = '',
  variant = 'outline',
  size = 'sm',
}: ShareCalculationButtonProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Generate deep link with query parameters
  const generateShareUrl = () => {
    const url = new URL(window.location.href);
    // Clear existing params
    url.search = '';
    
    // Add non-empty params
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    });

    return url.toString();
  };

  const shareUrl = typeof window !== 'undefined' ? generateShareUrl() : '';

  const handleCopy = async () => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const input = document.getElementById('share-url-input') as HTMLInputElement;
        if (input) {
          input.select();
          document.execCommand('copy');
        }
      }
      setCopied(true);
      toast.success('Calculation link copied to clipboard!');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Failed to copy link. Please copy manually.');
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${title} | CalculatorSheet`,
          text: `Check out this customized calculation on CalculatorSheet:`,
          url: shareUrl,
        });
      } catch (err) {
        // User cancelled or share failed, fallback to modal
        if ((err as Error).name !== 'AbortError') {
          setOpen(true);
        }
      }
    } else {
      setOpen(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        variant={variant}
        size={size}
        onClick={handleNativeShare}
        className={`gap-1.5 rounded-xl border-border/80 hover:bg-primary/10 hover:text-primary transition-all ${className}`}
        aria-label={`Share ${title} calculation`}
      >
        <Share2 className="h-3.5 w-3.5" />
        <span className="text-xs font-semibold">Share Calculation</span>
      </Button>

      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base font-bold">
            <LinkIcon className="h-4 w-4 text-primary" />
            Share This Calculation
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Anyone with this unique link will see your exact inputs and calculation results.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="flex items-center gap-2">
            <Input
              id="share-url-input"
              value={shareUrl}
              readOnly
              className="text-xs font-mono select-all h-9 bg-muted/40"
              aria-label="Shareable URL link"
            />
            <Button
              size="sm"
              onClick={handleCopy}
              className="gap-1.5 shrink-0 px-3 h-9"
              aria-label="Copy link to clipboard"
            >
              {copied ? (
                <>
                  <Check className="h-3.5 w-3.5 text-primary-foreground" />
                  <span className="text-xs">Copied</span>
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" />
                  <span className="text-xs">Copy Link</span>
                </>
              )}
            </Button>
          </div>

          <div className="rounded-xl bg-primary/5 p-3 border border-primary/20 text-xs text-muted-foreground leading-relaxed">
            💡 <strong className="text-foreground">Pro-Tip:</strong> Financial advisors, bloggers, and students use this link to embed or share specific loan and investment scenarios.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
